import { graphql } from "gatsby";
import React from "react";
import { BannerImage } from "../components/atoms/BannerImage";
import { Footer, Header } from "../components/layout/index";
import {
  BlogListing,
  Cta,
  Faq,
  Features,
  Hero,
  KeyFigures,
  KeyPoints,
  LogoCloudSection,
  PressListing,
  Roles,
  Security,
  Testimonials,
  DynamicContactForm,
  DynamicContactFormThanks,
  IframeElement,
  PricingSection,
} from "../components/sections";
import { Seo } from "../components/Seo";
import { lightBackgroundColor } from "../constants";
import { getOppositeBackgroundColor, toKebabCase } from "../utils";

/**
 * List of sanity types and their renderable components.
 */
const typeToComponent = {
  hero: Hero,
  keyFiguresSection: KeyFigures,
  keyPointsSection: KeyPoints,
  logoCloudSection: LogoCloudSection,
  rolesSection: Roles,
  dynamicPageTestimonials: Testimonials,
  dynamicPageBlogListing: BlogListing,
  pressListing: PressListing,
  security: Security,
  faqSection: Faq,
  ctaBanner: Cta,
  contactForm: DynamicContactForm,
  contactFormThanks: DynamicContactFormThanks,
  featuresSection: Features,
  iframeElement: IframeElement,
  bannerImage: BannerImage,
  pricingSection: PricingSection,
};

const typeToSpecificId = {
  hero: props => `${props.title}-${props.accentTitle}`,
  ctaBanner: props => `${props.titleLine1}-${props.titleLine2}`,
};

/**
 * List of sanity types where the background color switch is skipped, when they are the previous or next section to be rendered.
 */
const skipBackgroundColorSwitch = {
  previous: [],
  next: ["keyFiguresSection"],
};

/**
 * Compute the next section's background color, taking into account when we need to skip the switch,
 * depending on the previous and the next section type.
 */
const getNextBackgroundColor = ({
  currentBackgroundColor,
  previousSectionType,
  nextSectionType,
}) => {
  if (
    !previousSectionType ||
    skipBackgroundColorSwitch.previous.includes(previousSectionType) ||
    skipBackgroundColorSwitch.next.includes(nextSectionType)
  ) {
    return currentBackgroundColor;
  }
  return getOppositeBackgroundColor(currentBackgroundColor);
};

/**
 * Render the given section with the given background, with adapted props for the static components
 * @todo: this function should disappear once we adapt correctly the components' props
 */
const renderSection = ({ section, locale, backgroundColor }) => {
  const { _key, _type, ...props } = section;

  const ToRender = typeToComponent[_type];

  if (!ToRender) {
    return null;
  }

  props.locale = locale;

  if (_type === "keyFiguresSection") {
    props.keyFigures = [
      { ...props.keyFigure1, prefix: "+" },
      { ...props.keyFigure2, prefix: "+" },
      { ...props.keyFigure3, prefix: "+" },
    ];
  }
  if (_type === "keyPointsSection") {
    props.keyPoints = [
      { ...props.keyPoint1 },
      { ...props.keyPoint2 },
      { ...props.keyPoint3 },
    ];
  }

  const id = toKebabCase(
    typeToSpecificId[_type]?.(props) ?? props.title ?? `${_type}-${_key}`,
  );

  // Since we have a fixed banner, this is a trick to make the anchor scrolling correctly to the section
  const anchorOffsetClassname = "pt-[62px] mt-[-62px]";

  return (
    <>
      <div key={_key} id={id} className={anchorOffsetClassname}>
        <ToRender {...props} backgroundColor={backgroundColor} />
      </div>
    </>
  );
};

/**
 * Get all the listed types and render their respective component.
 * Alternate the background color of the sections except when explicitely skipped
 */
const PageSections = ({ locale, pageSections }) => {
  let backgroundColor = lightBackgroundColor;
  let previousSectionType = undefined;

  const renderedSections = [];

  pageSections.forEach(section => {
    backgroundColor = getNextBackgroundColor({
      currentBackgroundColor: backgroundColor,
      previousSectionType,
      nextSectionType: section._type,
    });
    const renderedSection = renderSection({ section, locale, backgroundColor });

    if (renderedSection !== null) {
      renderedSections.push(renderedSection);
      previousSectionType = section._type;
    }
  });

  return renderedSections;
};

/**
 * A Dynamic page. Given the list of sections, render the whole page correctly with the corresponding components, and with an alternating background.
 */
const DynamicPage = ({ data: { page }, pageContext: { id } }) => {
  const {
    locale,
    pageSections = [],
    title,
    seoDescription,
    seoKeywords,
  } = page;

  return (
    <>
      <Seo
        locale={locale}
        title={title}
        description={seoDescription}
        keywords={seoKeywords}
      />
      <Header locale={locale} />
      <PageSections locale={locale} pageSections={pageSections} />
      <Footer locale={locale} />
    </>
  );
};

export default DynamicPage;

export const pageQuery = graphql`
  query DynamicPagesBySlug($id: String) {
    page: sanityDynamicPage(id: { eq: $id }) {
      locale
      title
      seoDescription
      seoKeywords
      pageSections: _rawPageSections(resolveReferences: { maxDepth: 10 })
    }
  }
`;
